import React, { useEffect, useState } from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot } from '@fortawesome/free-solid-svg-icons';
import { SharePointData } from '../../types/sharepoint.types';
import { getSafeImageSrc } from '../../utils/imageUtils';
import SharePointConnectGuideModal from './SharePointConnectGuideModal';
import SharePointUrlGuideModal from './SharePointUrlGuideModal';

interface SharePointConnectionTabProps {
  selectedSharepointData: SharePointData | undefined;
  onUrlChange: (value: string) => void;
  onConnect: (sharepointId: number, source: 'click' | 'loop') => void;
  systemConfig: { Id: string; ConfigValue: string; }[];
  assistant: any;
}

const SharePointConnectionTab: React.FC<SharePointConnectionTabProps> = ({
  selectedSharepointData,
  onUrlChange,
  onConnect,
  systemConfig,
  assistant,
}) => {
  const [safeAiIconSrc, setSafeAiIconSrc] = useState<string>(assistant?.app_ai_face);
  const [showConnectGuide, setShowConnectGuide] = useState(false);
  const [showUrlGuide, setShowUrlGuide] = useState(false);

  const systemUser = systemConfig.find(item => item.Id === 'sharepoint_system_user')?.ConfigValue || '';

  // Update safe source when app_ai_face changes
  useEffect(() => {
    const updateSafeSource = async (): Promise<void> => {
      const src = assistant?.app_ai_face;
      const safeSrc = await getSafeImageSrc(src);
      setSafeAiIconSrc(safeSrc);
    };

    updateSafeSource();
  }, [assistant?.app_ai_face]);

  return (
    <>
      <div className="p-4 h-full">
        <div className="grid grid-cols-3 gap-1 p-4 border border-gray-300 rounded-md shadow-lg">
          {/* Left Column: Text Instructions */}
          <div className="col-span-2">
            <h3 className="text-sm font-semibold mb-6">
              Connect me with your SharePoint source.
              <br />
              You only have to do this once for this source.
            </h3>

            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-sm">
                  1
                </div>
                <div>
                  <p>Open a separate browser tab or window and navigate to your SharePoint source.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-sm">
                  2
                </div>
                <div>
                  <p>
                    Grant access to user <strong>{systemUser}</strong> on your SharePoint source with
                    at least <strong>Read</strong> permissions (e.g., <em>Member</em> or <em>Visitor</em> role).
                  </p>
                  <p className="text-gray-600 text-sm mt-1">
                    This allows the assistant to read content from your SharePoint site. Go to your SharePoint site's
                    <strong> Settings &gt; Site permissions</strong> and add <strong>{systemUser}</strong> as a member.
                  </p>
                  <button
                    type="button"
                    className="text-gray-500 text-sm hover:underline mt-1 block p-0 bg-transparent border-none"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowConnectGuide(true);
                    }}
                  >
                    How do I do that?
                  </button>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-sm">
                  3
                </div>
                <div className="flex-1">
                  <p>Enter the URL of your SharePoint source.</p>
                  <button
                    type="button"
                    className="text-gray-500 text-sm hover:underline mt-1 block p-0 bg-transparent border-none"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowUrlGuide(true);
                    }}
                  >
                    Where do I find that?
                  </button>
                  <div className="flex items-center space-x-4 mt-2">
                    <input
                      type="text"
                      value={selectedSharepointData?.url || ''}
                      onChange={(e) => onUrlChange(e.target.value)}
                      placeholder="Eg: https://nuwildcat.sharepoint.com/sites/sitename"
                      className="w-full flex-1 px-3 py-2 border rounded-md"
                      disabled={selectedSharepointData?.connectionState === 'connecting' || selectedSharepointData?.connectionState === 'connected'}
                    />
                  </div>
                  {selectedSharepointData?.UrlError && (
                    <p className="text-red-500 text-sm mt-2">{selectedSharepointData.UrlError}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Icons, Horizontal Dots, and Connect Button */}
          <div className="flex col-span-1 flex-col items-center justify-center space-y-4 h-full">
            <div className="flex items-center space-x-4">
              {/* Left Icon */}
              {assistant?.app_ai_face ? (
                <img
                  src={safeAiIconSrc}
                  alt="AI Face"
                  className="h-8 w-8 cursor-pointer"
                />
              ) : (
                <FontAwesomeIcon icon={faRobot} className="h-8 w-8 text-black" />
              )}

              {/* Horizontal Dots */}
              <div className="flex items-center space-x-2">
                {[...Array(5)].map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-1 w-1 rounded-full ${selectedSharepointData?.connectionState === 'connecting'
                      ? 'bg-black animate-pulse'
                      : 'bg-black'
                      }`}
                    style={
                      selectedSharepointData?.connectionState === 'connecting'
                        ? { animationDelay: `${idx * 150}ms` }
                        : {}
                    }
                  />
                ))}
              </div>
              {/* Right Icon */}
              <img src="/sharepoint-logo.png" alt="SharePoint Logo" className="h-8 w-8" />
            </div>

            {/* Connect Button */}
            {selectedSharepointData?.connectionState === 'idle' && (
              <button
                onClick={() => {
                  selectedSharepointData && onConnect(selectedSharepointData.id, 'click');
                }}
                className="bg-black text-white hover:text-white hover:border-b-4 hover:bg-black py-2 px-6 border rounded-full whitespace-nowrap"
              >
                CONNECT
              </button>
            )}

            {selectedSharepointData?.connectionState === 'connecting' && (
              <button
                className="pl-10 bg-blue-600 text-white py-2 px-8 rounded-md flex items-center space-x-4 cursor-not-allowed"
                disabled
              >
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Connecting...</span>
              </button>
            )}

            {selectedSharepointData?.connectionState === 'connected' && (
              <button
                className="bg-green-600 text-white py-2 px-6 rounded-md flex items-center space-x-2 cursor-not-allowed"
                disabled
              >
                <CheckCircle2 className="h-4 w-4" />
                <span>Connected</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* How do I do that? - Grant Access Guide */}
      <SharePointConnectGuideModal
        isOpen={showConnectGuide}
        onClose={() => setShowConnectGuide(false)}
      />

      {/* Where do I find that? - URL Guide */}
      <SharePointUrlGuideModal
        isOpen={showUrlGuide}
        onClose={() => setShowUrlGuide(false)}
      />
    </>
  );
};

export default SharePointConnectionTab;
